import type { FormEvent } from 'react'

import { useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PillButton } from '@/components/generals/pill-button'

interface NewsletterCardProps {
    className?: string
}

/** Moss-green card with a one-field newsletter sign up. Addresses are saved to the newsletter list. */
export const NewsletterCard = ({ className }: NewsletterCardProps) => {
    const [email, setEmail] = useState('')
    const [website, setWebsite] = useState('')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [done, setDone] = useState(false)

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)

        if (!/^\S+@\S+\.\S{2,}$/.test(email.trim())) return setError('That email address does not look quite right.')

        setSending(true)

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, website, source: 'footer' })
            })
            const data = await response.json().catch(() => ({}))

            if (!response.ok) {
                setError(data?.message ?? 'Your sign up did not go through. Please try once more.')
                return
            }

            setDone(true)
        }
        catch {
            setError('We could not connect just now. Check your internet and try again.')
        }
        finally {
            setSending(false)
        }
    }

    return (
        <div
            className={cn(
                'grain relative isolate overflow-hidden rounded-[30px] p-6 sm:p-8',
                'bg-[radial-gradient(120%_150%_at_0%_100%,#c5d86f_0%,#7c9a3c_28%,#34481a_62%,#121a0a_100%)]',
                className
            )}
        >
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />
            <div aria-hidden='true' className='motion-glow pointer-events-none absolute -right-16 -top-20 -z-10 size-64 rounded-full bg-[radial-gradient(circle,rgba(226,243,122,0.28),rgba(226,243,122,0)_70%)] blur-xl' />

            <h2 id='newsletter-title' className='text-2xl font-normal leading-tight tracking-[-0.035em] text-cream sm:text-[1.75rem]'>
                Read the <span className='em-serif'>Verdane letter</span>
            </h2>

            <p className='mt-2 max-w-sm text-sm leading-relaxed text-cream/70'>
                A single unhurried note each month: what moved in the markets, what is new here and one habit to try.
            </p>

            {done
                ? (
                    <p role='status' className='mt-6 flex items-center gap-3 text-sm text-cream'>
                        <span className='grid size-8 place-items-center rounded-full bg-citron text-black'>
                            <Check aria-hidden='true' className='size-4' />
                        </span>
                        Thank you, you are subscribed. Look for us next month.
                    </p>
                )
                : (
                    <form onSubmit={submit} noValidate aria-labelledby='newsletter-title' className='relative mt-6'>
                        <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
                            <label htmlFor='newsletter-website'>
                                Leave this field empty
                            </label>

                            <input
                                id='newsletter-website'
                                name='website'
                                type='text'
                                tabIndex={-1}
                                autoComplete='off'
                                value={website}
                                onChange={event => setWebsite(event.target.value)}
                            />
                        </div>

                        <div className='flex flex-col gap-3 transition-shadow sm:flex-row sm:items-center sm:rounded-full sm:bg-black/35 sm:p-1.5 sm:ring-1 sm:ring-white/10 sm:focus-within:ring-citron/50'>
                            <label htmlFor='newsletter-email' className='sr-only'>
                                Email address
                            </label>

                            <input
                                id='newsletter-email'
                                name='email'
                                type='email'
                                autoComplete='email'
                                required
                                placeholder='you@example.com'
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                aria-invalid={error ? true : undefined}
                                aria-describedby={error ? 'newsletter-error' : undefined}
                                className='h-12 min-w-0 flex-1 rounded-full bg-black/35 px-5 text-sm text-cream ring-1 ring-white/10 placeholder:text-cream/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-citron/60 sm:h-10 sm:bg-transparent sm:px-4 sm:ring-0 sm:focus-visible:ring-0'
                            />

                            <PillButton type='submit' size='sm' loading={sending} className='self-start sm:self-auto'>
                                Sign me up
                            </PillButton>
                        </div>

                        {error && (
                            <p id='newsletter-error' role='alert' className='mt-3 px-1 text-sm text-[#ffd2c4]'>
                                {error}
                            </p>
                        )}
                    </form>
                )}

            <p className='mt-5 text-xs text-cream/50'>
                Never any spam. At most one email a month.
            </p>
        </div>
    )
}
